import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './PenconnectInc.css';

// ── Logo ─────────────────────────────────────────────────────────────
// Swap <LogoMark> for <img src={penLogoSrc}...> once you add the image
function LogoMark({ size = 42 }) {
  return (
    <div className="pen-logo-mark" style={{ width: size, height: size, fontSize: size * 0.33 }}>
      PEN
    </div>
  );
}

// ── Reveal on scroll ─────────────────────────────────────────────────
function observeReveal(el) {
  if (!el) return () => {};
  const obs = new IntersectionObserver(([e]) => {
    if (e.isIntersecting) { el.classList.add('pen-vis'); obs.unobserve(el); }
  }, { threshold: 0.08 });
  obs.observe(el);
  return () => obs.disconnect();
}
function useReveal() {
  const ref = useRef(null);
  useEffect(() => observeReveal(ref.current), []);
  return ref;
}
function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ── What We Offer ────────────────────────────────────────────────────
const OFFERS = [
  {
    icon: '✍️',
    title: 'Content Creation',
    desc: 'Blogs, product copy, social media posts, video scripts and brand storytelling — content that connects emotionally and pushes customers to act.',
    tags: ['Blog Posts', 'Social Copy', 'Video Scripts', 'Product Descriptions'],
    large: true,
  },
  {
    icon: '🎨',
    title: 'Brand Identity',
    desc: 'Full brand systems — strategy, visual identity, tone of voice, and guidelines that make your D2C brand instantly recognisable.',
    tags: ['Brand Strategy', 'Visual System', 'Guidelines'],
    large: false,
  },
  {
    icon: '📣',
    title: 'Ad Creatives',
    desc: 'Scroll-stopping creatives for Meta, Google, and YouTube. Designed for performance, built to convert.',
    tags: ['Meta Ads', 'Google', 'YouTube'],
    large: false,
  },
  {
    icon: '🏆',
    title: 'Logo Design',
    desc: 'Strategic, scalable logos — wordmarks, emblems, icons — crafted to carry your brand for years.',
    tags: ['Wordmark', 'Emblem', 'Icon'],
    large: false,
  },
  {
    icon: '🏷️',
    title: 'Product Labelling',
    desc: 'Premium packaging and label design that builds trust on shelf and online, and turns first-time buyers into loyal customers.',
    tags: ['Packaging', 'Label Design', 'Print Ready'],
    large: true,
  },
];

const WHY_CARDS = [
  { n: '01', cls: 'wn1', title: 'D2C Native Thinking', desc: 'Every decision we make is shaped by what actually drives awareness, trust, and conversions for product brands.' },
  { n: '02', cls: 'wn2', title: 'Brand + Performance Together', desc: 'Your logo, ads, and copy all pull in the same direction — brand building and sales at once.' },
  { n: '03', cls: 'wn3', title: 'Fast Creative Turnaround', desc: 'Brief to delivery in days, not weeks. Polished assets, fast — without cutting corners.' },
  { n: '04', cls: 'wn4', title: 'Dedicated Team, Deep Focus', desc: 'You get a focused team that knows your product, your customers, and your growth goals inside out.' },
];

const STEPS = [
  { n: '01', cls: 'pp1', title: 'Discovery Brief',    desc: 'Share your brand, goals, and the creative work you need. The more context, the better the output.' },
  { n: '02', cls: 'pp2', title: 'Creative Strategy',  desc: 'We map messaging, visual direction, and approach before producing a single asset.' },
  { n: '03', cls: 'pp3', title: 'Production',         desc: 'Meticulous crafting of your content, designs, ads, logos, or labels.' },
  { n: '04', cls: 'pp4', title: 'Review & Refine',    desc: 'You review. We iterate fast until everything is exactly right.' },
  { n: '05', cls: 'pp5', title: 'Launch & Scale',     desc: 'Assets delivered and ready. We stay on hand for your next creative sprint.' },
];

const TICKER = [
  'Content Creation', 'Brand Identity', 'Ad Creatives', 'Logo Design',
  'Product Labelling', 'D2C Growth', 'Performance Creative', 'Brand Strategy',
];

const FORM_INFO = [
  { cls: 'fi1b', icon: '🔒', text: '100% confidential — your details stay private' },
  { cls: 'fi2b', icon: '⚡', text: 'Response within 24 business hours' },
  { cls: 'fi3b', icon: '🎯', text: 'Free creative strategy consultation' },
  { cls: 'fi4b', icon: '🏆', text: 'Trusted by 500+ D2C founders' },
];

const CATEGORIES = [
  'Beauty & Personal Care', 'Apparel & Fashion', 'Food & Beverage',
  'Health & Wellness', 'Home & Lifestyle', 'Consumer Electronics',
  'Jewellery & Accessories', 'Pet Products', 'Baby & Kids', 'Other',
];

// ════════════════════════════════════════════════════════════════════
export default function PenconnectInc() {
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [userName,    setUserName]    = useState('');
  const [form, setForm] = useState({ firstName:'', lastName:'', email:'', category:'', message:'' });

  useEffect(() => {
    const fn = () => setMenuOpen(false);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!form.firstName.trim()) { setSubmitError('Please enter your first name.'); return; }
    if (!form.email.trim())     { setSubmitError('Please enter your email address.'); return; }
    setSubmitting(true);
    try {
      await axios.post('/api/register', { ...form, source: 'D2C Creative Platform' });
      setUserName(form.firstName.trim());
      setSubmitted(true);
      setTimeout(() => scrollTo('pen-register'), 100);
    } catch (err) {
      if (err.response?.status === 409) {
        setSubmitError('This email is already registered! We will contact you shortly.');
      } else {
        setUserName(form.firstName.trim());
        setSubmitted(true);
        setTimeout(() => scrollTo('pen-register'), 100);
        try {
          const list = JSON.parse(localStorage.getItem('pen_leads') || '[]');
          list.push({ ...form, id: Date.now(), time: new Date().toLocaleString(), offline: true });
          localStorage.setItem('pen_leads', JSON.stringify(list));
        } catch (_) {}
      }
    } finally {
      setSubmitting(false);
    }
  }, [form]);

  const refs = Array.from({ length: 28 }, () => useRef(null));
  useEffect(() => {
    const cleans = refs.map(r => observeReveal(r.current));
    return () => cleans.forEach(fn => fn && fn());
  }, []);

  return (
    <div className="pen">

      {/* ── MOBILE MENU ── */}
      <div className={`pen-mobile-menu ${menuOpen ? 'open' : ''}`}>
        <button className="pen-mobile-close" onClick={() => setMenuOpen(false)} aria-label="Close">✕</button>
        {[['offer','What We Offer'],['why','Why Us'],['process','Process'],['pen-register','Get Started']].map(([id, label]) => (
          <a key={id} href={`#${id}`} onClick={() => { setMenuOpen(false); setTimeout(() => scrollTo(id), 50); }}>{label}</a>
        ))}
        <a href="https://profilevoice.com" target="_blank" rel="noopener noreferrer" className="pen-mobile-pv" onClick={() => setMenuOpen(false)}>
          <span className="pen-pv-badge">Content Creation</span>
          <span className="pen-pv-name">ProfileVoice</span> ↗
        </a>
      </div>

      {/* ── NAV ── */}
      <nav className="pen-nav">
        <div className="pen-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <LogoMark size={42} />
          <div className="pen-logo-text">
            <span className="pen-lt">Penconnect INC</span>
            <span className="pen-lt-sub">D2C Creative Platform</span>
          </div>
        </div>
        <div className="pen-nav-links">
          {[['#offer','What We Offer'],['#why','Why Us'],['#process','Process']].map(([href, label]) => (
            <a key={href} href={href}>{label}</a>
          ))}
          <a href="https://profilevoice.com" target="_blank" rel="noopener noreferrer" className="pen-nav-pv" title="Content creation with ProfileVoice">
            <span className="pen-pv-badge">Content Creation</span>
            <span className="pen-pv-name">ProfileVoice</span> ↗
          </a>
        </div>
        <a href="#pen-register" className="pen-nav-cta">Get Started Free →</a>
        <button className="pen-hamburger" onClick={() => setMenuOpen(true)} aria-label="Menu">
          <span/><span/><span/>
        </button>
      </nav>

      {/* ── HERO ── */}
      <section className="pen-hero">
        <div className="pen-hero-grid"/>
        <div className="pen-orbs">
          <div className="pen-orb pen-orb1"/><div className="pen-orb pen-orb2"/>
          <div className="pen-orb pen-orb3"/><div className="pen-orb pen-orb4"/>
        </div>
        <div className="pen-hero-inner">
          <div>
            <div className="pen-eyebrow"><div className="pen-pulse"/>Creative Studio · Content · Branding · Ads · Logos · Labelling</div>
            <h1 className="pen-h1">
              Stop Being<br/>
              <em>Invisible.</em><br/>
              Start Selling.
            </h1>
            <p className="pen-hsub">
              In a market of 5M+ brands, only the ones with sharp content, strong identity,
              and scroll-stopping creatives win. We build all of it — for you.
            </p>
            <div className="pen-ctas">
              <a href="#pen-register" className="pen-cta-p">Build My Brand →</a>
              <a href="#offer"        className="pen-cta-s">See What We Create</a>
            </div>
          </div>

          {/* Right side — offer preview */}
          <div className="pen-hero-r">
            <div className="pen-hero-r-label">What We Create</div>
            {[
              { icon:'✍️', bg:'rgba(245,158,11,.18)',  title:'Content Creation',    sub:'Copy · Scripts · Social Media' },
              { icon:'🎨', bg:'rgba(16,185,129,.2)',   title:'Brand Identity',      sub:'Strategy · Visual System' },
              { icon:'📣', bg:'rgba(59,130,246,.18)',  title:'Ad Creatives',        sub:'Meta · Google · YouTube' },
              { icon:'🏆', bg:'rgba(4,120,87,.18)',    title:'Logo Design',         sub:'Wordmark · Emblem · Icon' },
              { icon:'🏷️', bg:'rgba(110,231,183,.35)', title:'Product Labelling',   sub:'Packaging · Print · Digital' },
            ].map((s, i) => (
              <div key={i} className="pen-offer-pill">
                <div className="pen-op-icon" style={{ background: s.bg }}>{s.icon}</div>
                <div>
                  <div className="pen-op-title">{s.title}</div>
                  <div className="pen-op-sub">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="pen-ticker" aria-hidden="true">
        <div className="pen-ti">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="pen-titem">{t}</span>
          ))}
        </div>
      </div>

      {/* ── WHAT WE OFFER ── */}
      <section className="pen-offer" id="offer">
        <div className="pen-offer-inner">
          <div className="pen-section-head center pen-reveal" ref={refs[0]}>
            <div className="pen-chip">✦ What We Offer</div>
            <h2 className="pen-section-title">Five Ways We Make<br/><span>Your Brand Impossible to Ignore.</span></h2>
            <p className="pen-ss">
              Every creative asset your D2C brand needs — built with strategy,
              crafted with precision, designed to perform in the real market.
            </p>
          </div>
          <div className="pen-offer-grid">
            {OFFERS.map((offer, i) => (
              <div key={i} className={`pen-oc pen-reveal pen-d${i % 4}`} ref={refs[1 + i]}>
                <div className="pen-oc-blob"/>
                <div className="pen-oc-num">0{i + 1}</div>
                <span className="pen-oc-icon">{offer.icon}</span>
                <div className="pen-oc-title">{offer.title}</div>
                <div className="pen-oc-desc">{offer.desc}</div>
                <div className="pen-oc-tags">
                  {offer.tags.map(tag => (
                    <span key={tag} className="pen-oc-tag">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="pen-why" id="why">
        <div className="pen-why-inner">
          <div className="pen-reveal" ref={refs[6]}>
            <div className="pen-chip" style={{ background:'linear-gradient(135deg,#4ECDC4,#45B7D1)' }}>
              Why Choose Us
            </div>
            <h2 className="pen-section-title" style={{ color:'#0F3733', marginTop:16 }}>
              Not Just Creative.<br/>Creatives That Convert.
            </h2>
            <p className="pen-ss" style={{ color:'#2C5F5A' }}>
              Anyone can make something pretty. We make things that get noticed,
              build trust, and turn browsers into buyers — every single time.
            </p>
          </div>
          <div className="pen-wcards">
            {WHY_CARDS.map((card, i) => (
              <div key={i} className={`pen-wc pen-reveal pen-d${i}`} ref={refs[7 + i]}>
                <div className={`pen-wn ${card.cls}`}>{card.n}</div>
                <div>
                  <div className="pen-wct">{card.title}</div>
                  <div className="pen-wcd">{card.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="pen-proc" id="process">
        <div style={{ maxWidth:1120, margin:'0 auto' }}>
          <div className="pen-section-head center pen-reveal" ref={refs[11]}>
            <div className="pen-chip">How It Works</div>
            <h2 className="pen-section-title">From Brief to <span>Brand-Ready</span></h2>
            <p className="pen-ss">A clear five-step process that delivers high-quality assets, fast.</p>
          </div>
          <div className="pen-psteps">
            <div className="pen-pline"/>
            {STEPS.map((s, i) => (
              <div key={i} className={`pen-ps pen-reveal pen-d${i}`} ref={refs[12 + i]}>
                <div className={`pen-psn ${s.cls}`}>{s.n}</div>
                <div className="pen-pst">{s.title}</div>
                <div className="pen-psd">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM ── */}
      <div className="pen-fw" id="pen-register">
        <div className="pen-fin">
          <div className="pen-reveal" ref={useReveal()}>
            <div className="pen-chip">Get Started</div>
            <h2 className="pen-section-title" style={{ marginTop:16 }}>
              Your Brand Won't Build<br/><span>Itself. Let's Start.</span>
            </h2>
            <p className="pen-ss" style={{ marginBottom:8 }}>
              Tell us about your brand and what you need. Our creative team will reach out
              within 24 hours with a tailored proposal and strategy.
            </p>
            <div className="pen-flist">
              {FORM_INFO.map((f, i) => (
                <div key={i} className="pen-fi">
                  <div className={`pen-fii ${f.cls}`}>{f.icon}</div>
                  {f.text}
                </div>
              ))}
            </div>
          </div>

          <div className="pen-fcard pen-reveal pen-d1" ref={useReveal()}>
            {!submitted ? (
              <>
                <div className="pen-fct">Start Your Creative Project</div>
                <div className="pen-fcs">Fill in your details — we'll be in touch within 24 hours.</div>
                {submitError && <div className="pen-err">{submitError}</div>}
                <form onSubmit={handleSubmit} noValidate>
                  <div className="pen-frow">
                    <div className="pen-field">
                      <label>First Name *</label>
                      <input value={form.firstName} onChange={set('firstName')} placeholder="First name" required/>
                    </div>
                    <div className="pen-field">
                      <label>Last Name</label>
                      <input value={form.lastName} onChange={set('lastName')} placeholder="Last name"/>
                    </div>
                  </div>
                  <div className="pen-field">
                    <label>Email Address *</label>
                    <input type="email" value={form.email} onChange={set('email')} placeholder="you@yourbrand.com" required/>
                  </div>
                  <div className="pen-field">
                    <label>Product Category</label>
                    <select value={form.category} onChange={set('category')}>
                      <option value="">Select your product category...</option>
                      {CATEGORIES.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="pen-field">
                    <label>What Do You Need?</label>
                    <textarea rows={4} value={form.message} onChange={set('message')} placeholder="e.g. I'm launching a skincare brand and need a logo, brand identity, and ad creatives for Meta..."/>
                  </div>
                  <button type="submit" className="pen-fsub" disabled={submitting}>
                    {submitting ? '⏳ Submitting...' : '🚀 Start My Project →'}
                  </button>
                  <p className="pen-fnote">🔒 Your details are safe. We never share your information.</p>
                </form>
              </>
            ) : (
              <div className="pen-success">
                <div className="pen-success-icon">🎉</div>
                <h3>You're In, {userName}!</h3>
                <div className="pen-success-badge">✓ Enquiry received successfully</div>
                <p style={{ marginBottom:12 }}>
                  Our creative team will reach out within <strong>24 business hours</strong> with a
                  personalised strategy and project proposal.
                </p>
                <div className="pen-next-box">
                  <div className="pen-next-title">What happens next?</div>
                  <p>📞 We call to understand your brand and goals in depth</p>
                  <p>📋 We prepare a tailored creative strategy for you</p>
                  <p>🎨 We share a project proposal with timelines and pricing</p>
                </div>
                <p style={{ fontSize:13, color:'var(--ink3)', marginTop:12 }}>
                  Questions?{' '}
                  <a href="mailto:pen.connect.inc@gmail.com" style={{ color:'var(--c2)', fontWeight:600 }}>
                    pen.connect.inc@gmail.com
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="pen-footer">
        <div className="pen-ftin">
          <div>
            <div className="pen-flogo">
              <LogoMark size={40}/>
              <div style={{ display:'flex', flexDirection:'column', lineHeight:1.2 }}>
                <span className="pen-flt">Penconnect INC</span>
                <span style={{ fontSize:10, fontWeight:600, letterSpacing:'0.18em', textTransform:'uppercase', color:'rgba(255,255,255,0.32)', marginTop:3 }}>
                  D2C Creative Platform
                </span>
              </div>
            </div>
            <p className="pen-fdesc">
              The complete creative platform for D2C brands — content, branding, ads, logos, and labelling.
              Built for founders who want to grow.
            </p>
          </div>
          <div className="pen-fcol">
            <h4>Services</h4>
            <a href="#offer">Content Creation</a>
            <a href="#offer">Brand Identity</a>
            <a href="#offer">Ad Creatives</a>
            <a href="#offer">Logo Design</a>
            <a href="#offer">Product Labelling</a>
          </div>
          <div className="pen-fcol">
            <h4>Connect</h4>
            <a href="mailto:pen.connect.inc@gmail.com">pen.connect.inc@gmail.com</a>
            <a href="#pen-register">Start a Project</a>
            <a href="#process">How It Works</a>
            <a href="#why">Why PEN</a>
          </div>
          <div className="pen-fcol">
            <h4>Our Network</h4>
            <a href="https://profilevoice.com" target="_blank" rel="noopener noreferrer">ProfileVoice ↗</a>
          </div>
        </div>
        <div className="pen-fbot">
          <p>© {new Date().getFullYear()} Penconnect INC. All rights reserved.</p>
          <p>Made with <span>♥</span> for ambitious D2C brands</p>
        </div>
      </footer>

      {/* ── FLOATING BUTTONS ── */}
      <div className="pen-fabs">
        <a href="mailto:pen.connect.inc@gmail.com" className="pen-fab fab-ml" title="Email Us">✉️</a>
      </div>
    </div>
  );
}
